export interface BarListRow {
  key: string;
  label: string;
  value: number;
  /** Shown after the value, in smaller type: a share, a count of sessions. */
  note?: string;
}

interface BarListProps {
  rows: BarListRow[];
  formatValue: (value: number) => string;
  /** A CSS colour. One for the whole list: the rows differ in size, not kind. */
  color?: string;
  /** What the bars are measured against. Defaults to the largest row. */
  max?: number;
}

/**
 * Rows compared by the length of a bar. Each carries its own name and figure,
 * so nothing is read from colour and nothing waits for a pointer.
 */
export default function BarList({ rows, formatValue, color = 'var(--chart-1)', max }: BarListProps) {
  const largest = max ?? Math.max(0, ...rows.map((r) => r.value));

  return (
    <ul className="space-y-3">
      {rows.map((row) => {
        const share = largest > 0 ? Math.min(100, (row.value / largest) * 100) : 0;
        return (
          <li key={row.key}>
            <div className="flex items-baseline justify-between gap-3 text-sm">
              <span className="font-medium text-foreground truncate">{row.label}</span>
              <span className="shrink-0 tabular-nums text-foreground">
                {formatValue(row.value)}
                {row.note && <span className="ml-1.5 text-xs text-muted-foreground">{row.note}</span>}
              </span>
            </div>
            <div className="mt-1.5 h-2.5" aria-hidden="true">
              <div
                className="h-full rounded-r"
                style={{ width: `${share}%`, minWidth: row.value > 0 ? 2 : 0, background: color }}
              />
            </div>
          </li>
        );
      })}
    </ul>
  );
}
